/**
 * Centralized Query Key Factory
 *
 * WHY THIS FILE EXISTS:
 * ─────────────────────
 * TanStack Query uses query keys like a cache address. If two components use
 * different key shapes for the same data (e.g. ['leads'] vs ['lead', 'list'])
 * they get TWO separate cache entries → duplicate API calls.
 *
 * This file is the single source of truth for every cache key in the app.
 * All hooks import from here. No key is ever written twice.
 *
 * KEY HIERARCHY (enables targeted invalidation):
 * ─────────────────────────────────────────────
 * leads.all             → ['leads']
 *   leads.lists()       → ['leads', 'list']          ← invalidate after CREATE
 *     leads.list(f)     → ['leads', 'list', filters] ← specific page/filter combo
 *   leads.details()     → ['leads', 'detail']
 *     leads.detail(id)  → ['leads', 'detail', 5]     ← invalidate after UPDATE/DELETE
 *       leads.profile(id) → ['leads', 'detail', 5, 'profile']
 *
 * INVALIDATION STRATEGY:
 *   After CREATE  → invalidate lists()           (new row appears in all lists)
 *   After UPDATE  → invalidate detail(id) + lists()
 *   After DELETE  → removeQueries detail(id) + invalidate lists()
 */

export const queryKeys = {

    // ── Leads ──────────────────────────────────────────────────────────────────
    leads: {
        all:         ()          => ["leads"],
        lists:       ()          => [...queryKeys.leads.all(), "list"],
        list:        (filters)   => [...queryKeys.leads.lists(), filters],
        dormant:     (filters)   => [...queryKeys.leads.all(), "dormant", filters],
        statusFlags: ()          => [...queryKeys.leads.all(), "status-flags"],
        details:     ()          => [...queryKeys.leads.all(), "detail"],
        detail:      (id)        => [...queryKeys.leads.details(), id],
        profile:     (id)        => [...queryKeys.leads.detail(id), "profile"],
        orders:      (id, p)     => [...queryKeys.leads.detail(id), "orders", p],
        timeline:    (id, p)     => [...queryKeys.leads.detail(id), "timeline", p],
        locations:   (id)        => [...queryKeys.leads.detail(id), "locations"],
    },

    // ── Customers ──────────────────────────────────────────────────────────────
    customers: {
        all:     ()        => ["customers"],
        lists:   ()        => [...queryKeys.customers.all(), "list"],
        list:    (filters) => [...queryKeys.customers.lists(), filters],
        details: ()        => [...queryKeys.customers.all(), "detail"],
        detail:  (id)      => [...queryKeys.customers.details(), id],
    },

    // ── Products ───────────────────────────────────────────────────────────────
    products: {
        all:            ()        => ["products"],
        lists:          ()        => [...queryKeys.products.all(), "list"],
        list:           (filters) => [...queryKeys.products.lists(), filters],
        details:        ()        => [...queryKeys.products.all(), "detail"],
        detail:         (id)      => [...queryKeys.products.details(), id],
        priceHistory:   (id)      => [...queryKeys.products.detail(id), "price-history"],
        customizations: (id)      => [...queryKeys.products.detail(id), "customizations"],
    },

    // ── Orders ─────────────────────────────────────────────────────────────────
    orders: {
        all:     ()          => ["orders"],
        lists:   ()          => [...queryKeys.orders.all(), "list"],
        list:    (filters)   => [...queryKeys.orders.lists(), filters],
        details: ()          => [...queryKeys.orders.all(), "detail"],
        detail:  (id)        => [...queryKeys.orders.details(), id],
        items:   (orderId)   => [...queryKeys.orders.detail(orderId), "items"],
    },

    // ── Users ──────────────────────────────────────────────────────────────────
    users: {
        all:     ()        => ["users"],
        lists:   ()        => [...queryKeys.users.all(), "list"],
        list:    (filters) => [...queryKeys.users.lists(), filters],
        details: ()        => [...queryKeys.users.all(), "detail"],
        detail:  (id)      => [...queryKeys.users.details(), id],
    },

    // ── Team ───────────────────────────────────────────────────────────────────
    team: {
        all:       ()    => ["team"],
        members:   ()    => [...queryKeys.team.all(), "members"],
        myTl:      ()    => [...queryKeys.team.all(), "my-tl"],
        teamLeads: (p)   => [...queryKeys.team.all(), "team-leads", p],
        byTl:      (id, p) => [...queryKeys.team.all(), "by-tl", id, p],
    },

    // ── Visits ─────────────────────────────────────────────────────────────────
    visits: {
        all:          ()        => ["visits"],
        lists:        ()        => [...queryKeys.visits.all(), "list"],
        list:         (filters) => [...queryKeys.visits.lists(), filters],
        details:      ()        => [...queryKeys.visits.all(), "detail"],
        detail:       (id)      => [...queryKeys.visits.details(), id],
        logs:         (id)      => [...queryKeys.visits.detail(id), "logs"],
        adminSummary: (date)    => [...queryKeys.visits.all(), "admin-summary", date],
    },

    // ── Call Admin ─────────────────────────────────────────────────────────────
    callAdmin: {
        all:         ()          => ["call-admin"],
        logs:        ()          => [...queryKeys.callAdmin.all(), "logs"],
        logsList:    (filters)   => [...queryKeys.callAdmin.logs(), "list", filters],
        queue:       ()          => [...queryKeys.callAdmin.all(), "queue"],
        queueList:   (filters)   => [...queryKeys.callAdmin.queue(), "list", filters],
        queueCounts: (base)      => [...queryKeys.callAdmin.queue(), "counts", base],
        crmUsers:    ()          => [...queryKeys.callAdmin.all(), "crm-users"],
        overview:    (date)      => [...queryKeys.callAdmin.all(), "overview", date],
        agentQueue:  (p)         => [...queryKeys.callAdmin.all(), "agent-queue", p],
        agentLogs:   (p)         => [...queryKeys.callAdmin.all(), "agent-logs", p],
        agentOrders: (p)         => [...queryKeys.callAdmin.all(), "agent-orders", p],
    },

    // ── Daily Tracker ──────────────────────────────────────────────────────────
    dailyTracker: {
        all:    ()              => ["daily-tracker"],
        list:   (p)             => [...queryKeys.dailyTracker.all(), "list", p],
        detail: (date, userId)  => [...queryKeys.dailyTracker.all(), "detail", date, userId],
    },

    // ── Dashboard ──────────────────────────────────────────────────────────────
    dashboard: {
        all:   ()  => ["dashboard"],
        stats: (p) => [...queryKeys.dashboard.all(), "stats", p],
    },

    // ── Roles & Areas ──────────────────────────────────────────────────────────
    roles: {
        all:  () => ["roles"],
        list: () => [...queryKeys.roles.all(), "list"],
    },
    areas: {
        all:  () => ["areas"],
        list: () => [...queryKeys.areas.all(), "list"],
    },
};
